import { watch } from 'vue';
import useCommonStore from '@/common/store/pinia/common';
import getClusterId from '@/common/store/pinia/storageCluster/getClusterId';
import { defineStore } from 'pinia';
import { AbgwMigrationsSummary } from '@schema';
import i18n from '@/common/i18n';
import { NotificationType } from '@/common/constants/notifications.constants';
import { useNotifications } from '@/common/utils/notifications/notificationsPlugin';
import { useNotificationsStore } from '@/common/store/pinia/notifications';
import useAbgwUpstreamsStore from '@/common/store/pinia/abgwUpstreams';
import abgwNotificationHandler from '@/common/utils/abgwNotificationHandler';
import takeFirst from '@/common/utils/takeFirst';
import abgwMigrationsMapper, {
  AbgwMigrationsSummaryItem,
  AbgwMigrationsSummaryRaw
} from '@/common/store/pinia/abgwMigrations/abgwMigrationsMapper';

const getUrl = async () => {
  const clusterId = await getClusterId();
  return `/api/v2/${clusterId}/abgw/reverse-proxying/migrations-summary`;
};

const NOTIFICATION_ID_PREFIX = 'abgw_migration_';

const getNotificationId = (migrationId: string) => `${NOTIFICATION_ID_PREFIX}${migrationId}`;

export const useAbgwMigrationsStore = defineStore('abgwMigrations', () => {
  const commonStore = useCommonStore<AbgwMigrationsSummaryRaw, AbgwMigrationsSummaryItem>({
    getUrl,
    plural: (data: AbgwMigrationsSummary[]) => data.map(item => ({
      ...item,
      id: `${item.src_upstream}~${item.dst_upstream}`
    })),
    mapItem: abgwMigrationsMapper
  })();

  const syncNotifications = (migrations: AbgwMigrationsSummaryItem[]) => {
    const { notify } = useNotifications();
    const notificationsStore = useNotificationsStore();
    const activeIds = new Set(migrations.map(migration => getNotificationId(migration.id)));

    migrations.forEach((migration) => {
      notify({
        id: getNotificationId(migration.id),
        type: NotificationType.PROGRESS,
        progress: migration.size ? Math.min(100, Math.round(migration.transmitted / migration.size * 100)) : 0,
        title: i18n.t('Account migration'),
        message: i18n.t('Accounts to migrate: {count}. From "{src}" to "{dst}"', {
          count: migration.nr_migrations,
          src: migration.src_upstream,
          dst: migration.dst_upstream }),
        showClose: true
      });
    });

    notificationsStore.getItems
      .filter(notification => notification.id.startsWith(NOTIFICATION_ID_PREFIX) && !activeIds.has(notification.id))
      .forEach(notification => notificationsStore.remove(notification.id));
  };

  let notificationsInitialized = false;

  const initNotifications = async () => {
    if (notificationsInitialized) return;
    notificationsInitialized = true;

    const abgwUpstreamsStore = useAbgwUpstreamsStore();

    try {
      await takeFirst(abgwUpstreamsStore.fetchItems);
      await takeFirst(commonStore.fetchItems);
    } catch (error) {
      // backup storage reverse proxying may be unavailable or not configured
      notificationsInitialized = false;
      return;
    }

    watch(commonStore.getItems, syncNotifications, { immediate: true });
  };

  const onAbgw = abgwNotificationHandler('migration', {
    changed: () => takeFirst(commonStore.fetchItems)
  });

  return {
    ...commonStore,
    initNotifications,
    onAbgw
  };
}, {
  ws: {
    abgw: 'onAbgw'
  }
});

export default useAbgwMigrationsStore;
